import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { create, destroy, invalidator, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { theme } from '@dojo/widgets/middleware/theme';
import { createNlsMiddleware, userstate } from '@holmescorp/lcc-state';
import { logQuiz } from '@holmescorp/logger';
import { publish } from '@holmescorp/pub-sub';
import css from '../css/loading.m.css';
import { svc as auditsvc } from '../support/audit-svc';
import { svc } from '../support/qfsvc';
import QuestionList from '../support/questionlist';
import quizsvc from '../support/quizsvc';
import { infoChange, loadQuizProcess, quizChange, quizstate, quizstores, setInreview, setIsaudit, setProgressionIsrunning, setQuestion, setTitle } from '../support/state';
import { recordComplete, recordProgress, recordStart } from '../support/xapi';
const icache = createICacheMiddleware();
const nls = createNlsMiddleware('quiz');
const factory = create({ icache, nls, destroy, invalidator, quizstate, userstate, theme });
export const createProgressionMiddleware = () => {
    const progressionMiddleware = factory(({ id, middleware: { icache, nls, invalidator, quizstate, userstate, theme } }) => {
        const stores = [];
        quizstores.forEach((quizstore) => stores.push(quizstore));
        quizstate.setStore(stores[stores.length]);
        icache.set('svc', svc);
        icache.set('auditsvc', auditsvc);
        icache.set('startquiz', { cursor: 0, status: 'question' });
        icache.set('quizstore', stores[stores.length]);
        const properties = () => {
            const { get, path } = quizstate;
            const quiz = get(path('quiz'));
            const quizid = (quiz && quiz.id) || '';
            return {
                quizid,
                quiz,
                info: get(path('info')),
                quizurl: get(path('quizurl')),
                isrunning: get(path('progressionState', 'isrunning')),
                stopLoading: get(path('stopLoading')),
                instancekey: get(path('instancekey'))
            };
        };
        const api = {
            get startquiz() {
                return icache.get('startquiz');
            },
            set startquiz(val) {
                icache.set('startquiz', val);
            },
            get svc() {
                return icache.get('svc');
            },
            set svc(val) {
                icache.set('svc', val);
            },
            get auditsvc() {
                return icache.get('auditsvc');
            },
            set auditsvc(val) {
                icache.set('auditsvc', val);
            },
            get quizstore() {
                return icache.get('quizstore');
            },
            set quizstore(val) {
                quizstate.setStore(val);
                icache.set('quizstore', val);
            }
        };
        api.setStore = (store) => {
            icache.set('quizstore', store);
            quizstate.setStore(store);
        };
        api.setAuditsvc = (svc) => icache.set('auditsvc', svc);
        api.getProperties = properties;
        api.initialize = () => {
            icache.getOrSet('inited', () => {
                const { stopLoading } = properties();
                stopLoading && stopLoading();
                quizstate.run(setProgressionIsrunning)({ isrunning: '' });
                return true;
            });
        };
        api.load_quiz = async () => {
            logQuiz.debug(() => `progression.load_quiz() entry`);
            const { quizurl } = properties();
            const svc = icache.get('svc');
            svc._smd.url = quizurl;
            icache.set('svc', svc);
            const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
            const { title } = quiz;
            quizstate.run(loadQuizProcess)({ title, quiz, info, question: undefined });
            invalidator();
            logQuiz.debug(() => `progression.load_quiz() exit`);
            return true;
        };
        api.quiz_startup = async (isRestart = false) => {
            logQuiz.debug(() => `progression.quiz_startup() entry`);
            const svc = icache.get('svc');
            const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
            quizstate.run(setProgressionIsrunning)({ isrunning: 'quiz' });
            quizstate.run(quizChange)({ quiz });
            quizstate.run(infoChange)({ info });
            quizstate.run(setQuestion)({ question: undefined });
            info.runningtime && !isRestart
                ? recordProgress(quiz, info, undefined, 0)
                : recordStart(quiz, isRestart);
            invalidator();
            publish('quiz:status-change', { quizid: quiz.id });
            logQuiz.debug(() => `progression.quiz_startup() exit`);
            return true;
        };
        api.finalize = async () => {
            const { get, path } = userstate;
            const svc = icache.get('svc');
            const userid = get(path('user_id'));
            quizstate.run(setProgressionIsrunning)({ isrunning: 'final' });
            const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
            quizstate.run(quizChange)({ quiz });
            quizstate.run(infoChange)({ info });
            quizstate.run(setQuestion)({ question: undefined });
            invalidator();
            await quizsvc.update_annotation({ userid });
            publish('quiz:status-change', { quizid: quiz.id, status: 'final' });
            quiz.kind === 'activity' && recordComplete(info.lastresult, {}, quiz);
            return true;
        };
        api.render_loading = () => {
            const themeCss = theme.classes(css);
            const label = 'loading';
            const live = 'assertive';
            return [
                v('div', {
                    key: `spinner-${id}`,
                    classes: [themeCss.loading],
                    ...formatAriaProperties({ label, live })
                }, [v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })])
            ];
        };
        api.audit_init = () => {
            const { quizurl } = properties();
            const auditsvc = icache.get('auditsvc');
            logQuiz.info(`run_audit quizurl = "${quizurl}"`);
            auditsvc._smd.url = quizurl;
        };
        api.audit_startup = async () => {
            const { messages: { audit: auditText = '' } } = nls();
            const { quiz } = properties();
            quizstate.run(setProgressionIsrunning)({ isrunning: 'audit' });
            quizstate.run(setTitle)({ title: `${quiz ? quiz.title : ''} ${auditText}` });
            quizstate.run(setQuestion)({ question: undefined });
            invalidator();
            return true;
        };
        api.run_audit = () => {
            const { quiz = {}, info } = properties();
            const auditsvc = icache.get('auditsvc');
            const resultid = info && info.lastresult.id;
            const startquiz = icache.get('startquiz');
            const finalize = () => {
                quizstate.run(setIsaudit)({ isaudit: false });
                quizstate.run(setInreview)({ inreview: false });
                quizstate.run(setTitle)({ title: quiz.title });
                quizstate.run(setProgressionIsrunning)({ isrunning: '' });
                invalidator();
            };
            return [
                w(QuestionList, {
                    svc: auditsvc,
                    finalize,
                    resultid,
                    startquiz,
                    quizstore: quizstate.getStore()
                })
            ];
        };
        return api;
    });
    return progressionMiddleware;
};
